/**
 * **La foto, liviana antes de subir** (ronda 20-oct, `c-fotos`). Una foto de celular mide 4000 px y pesa
 * 3–6 MB: choca con el tope de 2 MB del servidor (`pedidos/imagenes.ts`) y tarda en subir. Aquí se
 * reduce a 2000 px por su lado mayor —se ve nítida en pantalla y en el PDF— y se guarda en JPEG de buena
 * calidad; si tiene partes transparentes, en WebP (el JPEG las volvería negras).
 *
 * **Nunca empeora lo que se elige:** si la foto ya cabe en 2000 px y es JPEG o WebP, o la reducida no
 * pesa menos, va la original; si el navegador no la puede leer (un formato raro), va la original y el
 * servidor decide como siempre. No cambia qué se manda ni a dónde: solo el archivo es más chico.
 */

export const LADO_MAYOR = 2000;
/** La calidad del JPEG/WebP: sin defectos a la vista y la mitad del peso de 0,95. */
export const CALIDAD = 0.88;

/** Las medidas a `lado` px por el lado mayor, sin agrandar nunca. */
export function medidaReducida(ancho: number, alto: number, lado = LADO_MAYOR): { ancho: number; alto: number } {
  const k = Math.min(1, lado / Math.max(ancho, alto));
  return { ancho: Math.max(1, Math.round(ancho * k)), alto: Math.max(1, Math.round(alto * k)) };
}

/** ¿Algún punto no es del todo opaco? Solo se mira en PNG, WebP y GIF: un JPEG nunca lo tiene. */
export function tieneTransparencia(rgba: Uint8ClampedArray): boolean {
  for (let i = 3; i < rgba.length; i += 4) if (rgba[i]! < 255) return true;
  return false;
}

const EXTENSION: Record<string, string> = { "image/jpeg": "jpg", "image/webp": "webp", "image/png": "png" };
const PUEDE_TRANSPARENCIA = ["image/png", "image/webp", "image/gif"];
const YA_COMPRIMIDA = ["image/jpeg", "image/webp"];

function aBlob(lienzo: HTMLCanvasElement, tipo: string): Promise<Blob | null> {
  return new Promise((ok) => lienzo.toBlob(ok, tipo, CALIDAD));
}

/** La foto pintada a su medida nueva, en JPEG o (si tiene transparencia) en WebP. */
async function codificar(foto: ImageBitmap, medida: { ancho: number; alto: number }, tipo: string): Promise<Blob | null> {
  const lienzo = document.createElement("canvas");
  lienzo.width = medida.ancho;
  lienzo.height = medida.alto;
  const ctx = lienzo.getContext("2d");
  if (!ctx) return null;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(foto, 0, 0, medida.ancho, medida.alto);
  const transparente = PUEDE_TRANSPARENCIA.includes(tipo) && tieneTransparencia(ctx.getImageData(0, 0, medida.ancho, medida.alto).data);
  // Un navegador que no sabe hacer WebP devuelve PNG: también guarda la transparencia.
  return aBlob(lienzo, transparente ? "image/webp" : "image/jpeg");
}

/** La foto reducida (o la original si no se gana nada o no se puede leer). Nunca lanza. */
export async function reducirFoto(archivo: File, { ladoMayor = LADO_MAYOR }: { ladoMayor?: number } = {}): Promise<File> {
  if (!archivo.type.startsWith("image/") || typeof createImageBitmap !== "function") return archivo;
  try {
    const foto = await createImageBitmap(archivo);
    const medida = medidaReducida(foto.width, foto.height, ladoMayor);
    const reducida = medida.ancho < foto.width;
    // Ya comprimida y del tamaño justo: se deja como está (volver a comprimirla solo la desmejora).
    const blob = !reducida && YA_COMPRIMIDA.includes(archivo.type) ? null : await codificar(foto, medida, archivo.type);
    foto.close?.();
    if (!blob || !EXTENSION[blob.type] || (!reducida && blob.size >= archivo.size)) return archivo;
    const nombre = `${(archivo.name || "foto").replace(/\.[^.]+$/, "")}.${EXTENSION[blob.type]}`;
    return new File([blob], nombre, { type: blob.type, lastModified: Date.now() });
  } catch {
    return archivo;
  }
}
